import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  VStack, HStack, Text, Box, Center, Spinner, Progress,
} from 'native-base';
import { Appbar, Card, IconButton } from 'react-native-paper';
import {
  Linking, Platform, ScrollView, StyleSheet,
} from 'react-native';
import Constants from 'expo-constants';
import { format } from 'date-fns';
import { TripsForEvent, TRIP_ROUTE_GEOMETRY } from '../../gql/trip/query';
import { calculateDistance } from '../../utils/distance';
import { decodePolyline } from '../../utils/polyline';
import TripMap from '../../components/maps/TripMap';
import TripProgressSummary from '../../components/trip/TripProgressSummary';
import TripPauseControls from '../../components/trip/TripPauseControls';
import { useLocationTrackingContext } from '../../context/LocationTrackingContext';

const POLL_INTERVAL = 60000; // Poll every 60 seconds

const STATUS_LABELS = {
  SCHEDULED: 'Planifié',
  IN_PROGRESS: 'En cours',
  COMPLETED: 'Terminé',
  CANCELLED: 'Annulé',
};

const styles = StyleSheet.create({
  mapContainer: {
    height: 300,
    borderRadius: 8,
  },
  // Web shows a server-side image with its own 4:3 ratio, so no fixed height (no cropping).
  mapContainerWeb: {
    borderRadius: 8,
  },
});

function TripTrackingScreen({ route, navigation }) {
  const { tripId, eventId } = route.params;
  const { data, loading, error } = useQuery(TripsForEvent, {
    variables: { eventId: Number(eventId) },
    pollInterval: POLL_INTERVAL,
  });

  const trip = data?.tripsForEvent?.find((t) => t.id === Number(tripId));
  // The driver of the trip in progress is the one tracked on this device.
  const { activeTrip } = useLocationTrackingContext();
  const isDriverOfTrip = !!trip && trip.status === 'IN_PROGRESS' && activeTrip?.id === trip.id;

  // Coordinates and route are stored by the API (computed once per trip), nothing is geocoded
  // from the device. The route also feeds the remaining distance and estimated arrival.
  const departureCoords = useMemo(() => (trip?.departureLatitude != null
    ? { lat: trip.departureLatitude, lon: trip.departureLongitude } : null), [trip]);
  const arrivalCoords = useMemo(() => (trip?.arrivalLatitude != null
    ? { lat: trip.arrivalLatitude, lon: trip.arrivalLongitude } : null), [trip]);

  const { data: routeGeometryData, loading: loadingRoute } = useQuery(TRIP_ROUTE_GEOMETRY, {
    variables: { tripId: Number(tripId) },
    skip: !trip,
  });
  const routeData = useMemo(() => {
    const geometry = routeGeometryData?.tripRouteGeometry;
    return geometry
      ? { coordinates: decodePolyline(geometry), distance: trip?.distanceInKm }
      : null;
  }, [routeGeometryData, trip?.distanceInKm]);

  // Get the most recent driver location (API now returns only the last one)
  const lastLocation = trip?.driverLocations?.[0];
  // Server-side cached map image (web), shared with every other viewer of this trip.
  const apiUrl = Constants.expoConfig?.extra?.EXPO_PUBLIC_API_URL ?? '';
  const mapUrl = trip?.mapUrl
    ? `${apiUrl}${trip.mapUrl}?t=${lastLocation?.timestamp ?? ''}` : undefined;

  // Calculate progress percentage based on remaining distance to destination
  const progress = useMemo(() => {
    if (!lastLocation || !departureCoords || !arrivalCoords) return 0;

    const totalDistance = calculateDistance(
      departureCoords.lat,
      departureCoords.lon,
      arrivalCoords.lat,
      arrivalCoords.lon,
    );

    // If departure and arrival are too close, consider trip complete
    if (totalDistance < 0.1) return 100;

    const distanceRemaining = calculateDistance(
      lastLocation.latitude,
      lastLocation.longitude,
      arrivalCoords.lat,
      arrivalCoords.lon,
    );

    // Progress = (total - remaining) / total
    // This correctly handles off-route positions
    const percentage = Math.min(((totalDistance - distanceRemaining) / totalDistance) * 100, 100);
    return Math.max(0, percentage);
  }, [lastLocation, departureCoords, arrivalCoords]);

  // Show loading if we're loading and don't have trip data yet
  if (loading && !trip) {
    return (
      <Center flex={1}>
        <Spinner size="lg" />
        <Text mt={4}>Chargement du trajet...</Text>
      </Center>
    );
  }

  // Only show error if there's an actual error or loading is complete and trip not found
  if (error) {
    return (
      <Center flex={1}>
        <Text color="red.500">
          Erreur:
          {' '}
          {error.message}
        </Text>
      </Center>
    );
  }

  if (!loading && !trip) {
    return (
      <Center flex={1}>
        <Text color="red.500">Erreur: Trajet non trouvé</Text>
      </Center>
    );
  }

  // If still loading but we have previous trip data, continue showing the screen
  if (!trip) {
    return (
      <Center flex={1}>
        <Spinner size="lg" />
        <Text mt={4}>Chargement du trajet...</Text>
      </Center>
    );
  }

  const driverName = trip.driver
    ? `${trip.driver.firstName} ${trip.driver.lastName}`
    : trip.guestDriverName || 'Inconnu';

  const openInMaps = () => {
    if (!lastLocation) return;
    const url = `https://www.google.com/maps/search/?api=1&query=${lastLocation.latitude},${lastLocation.longitude}`;
    Linking.openURL(url);
  };

  const openRouteInMaps = () => {
    if (!departureCoords || !arrivalCoords) return;
    const url = `https://www.google.com/maps/dir/?api=1&origin=${departureCoords.lat},${departureCoords.lon}&destination=${arrivalCoords.lat},${arrivalCoords.lon}&travelmode=driving`;
    Linking.openURL(url);
  };

  return (
    <>
      <Appbar.Header>
        <Appbar.BackAction onPress={() => navigation.goBack()} />
        <Appbar.Content title="Suivi du trajet" />
        <Appbar.Action icon="directions" onPress={openRouteInMaps} />
        {lastLocation && (
          <Appbar.Action icon="map-marker" onPress={openInMaps} />
        )}
      </Appbar.Header>

      <ScrollView style={{ flex: 1, backgroundColor: '#f3f4f6' }}>
        <VStack p={4} space={4}>
          {/* Trip Info Card */}
          <Card>
            <Card.Title
              title={trip.transportation?.name || 'Transport'}
              subtitle={`Conducteur: ${driverName}`}
            />
            <Card.Content>
              <VStack space={2}>
                <HStack justifyContent="space-between">
                  <Text fontSize="sm" color="gray.600">Départ:</Text>
                  <Text fontSize="sm" fontWeight="bold" flex={1} textAlign="right" numberOfLines={2}>
                    {trip.departureLocation}
                  </Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="sm" color="gray.600">Arrivée:</Text>
                  <Text fontSize="sm" fontWeight="bold" flex={1} textAlign="right" numberOfLines={2}>
                    {trip.arrivalLocation}
                  </Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="sm" color="gray.600">Heure de départ:</Text>
                  <Text fontSize="sm">
                    {format(new Date(trip.departureDateTime), 'dd/MM/yyyy HH:mm')}
                  </Text>
                </HStack>
                {(routeData?.distance || trip.distanceInKm) && (
                  <HStack justifyContent="space-between">
                    <Text fontSize="sm" color="gray.600">Distance:</Text>
                    <Text fontSize="sm">
                      {(routeData?.distance || trip.distanceInKm).toFixed(1)}
                      {' '}
                      km
                    </Text>
                  </HStack>
                )}
              </VStack>
            </Card.Content>
          </Card>

          {/* Map and Progress Card */}
          <Card>
            <Card.Title
              title="Position du conducteur"
              subtitle={trip.status === 'IN_PROGRESS'
                ? 'Mise à jour automatique toutes les 30s'
                : `Statut: ${STATUS_LABELS[trip.status] || trip.status}`}
            />
            <Card.Content>
              {trip.status !== 'IN_PROGRESS' && (
                <Center py={8}>
                  <Text color="gray.500">
                    Le suivi GPS est disponible uniquement pendant le trajet
                  </Text>
                </Center>
              )}
              {trip.status === 'IN_PROGRESS' && loadingRoute && (
                <Center py={8}>
                  <Spinner />
                  <Text mt={2} color="gray.500">Chargement de l&apos;itinéraire...</Text>
                </Center>
              )}
              {trip.status === 'IN_PROGRESS' && !loadingRoute && (
                <VStack space={3}>
                  {/* Progress Bar */}
                  {lastLocation && (
                    <VStack space={1}>
                      <HStack justifyContent="space-between" alignItems="center">
                        <Text fontSize="sm" color="gray.600">Progression:</Text>
                        <Text fontSize="sm" fontWeight="bold" color="blue.600">
                          {progress.toFixed(0)}
                          %
                        </Text>
                      </HStack>
                      <Progress
                        value={progress}
                        colorScheme="blue"
                        size="md"
                        bg="gray.200"
                      />
                    </VStack>
                  )}

                  {/* Map */}
                  <Box borderRadius="md" overflow="hidden" style={Platform.OS === 'web' ? styles.mapContainerWeb : styles.mapContainer}>
                    <TripMap
                      departureCoords={departureCoords}
                      arrivalCoords={arrivalCoords}
                      lastLocation={lastLocation}
                      routeData={routeData}
                      mapUrl={mapUrl}
                      trip={trip}
                    />
                  </Box>

                  {/* Legend */}
                  <HStack space={4} justifyContent="center">
                    <HStack alignItems="center" space={1}>
                      <Box w={3} h={3} bg="green.500" borderRadius="full" />
                      <Text fontSize="xs" color="gray.600">Départ</Text>
                    </HStack>
                    <HStack alignItems="center" space={1}>
                      <Box w={3} h={3} bg="blue.500" borderRadius="full" />
                      <Text fontSize="xs" color="gray.600">Conducteur</Text>
                    </HStack>
                    <HStack alignItems="center" space={1}>
                      <Box w={3} h={3} bg="red.500" borderRadius="full" />
                      <Text fontSize="xs" color="gray.600">Arrivée</Text>
                    </HStack>
                  </HStack>

                  {isDriverOfTrip && <TripPauseControls trip={trip} />}

                  <TripProgressSummary
                    trip={trip}
                    routeGeometry={routeGeometryData?.tripRouteGeometry}
                    lastLocation={lastLocation}
                    arrivalCoords={arrivalCoords}
                  />

                  {/* Last update info */}
                  {lastLocation ? (
                    <HStack justifyContent="space-between" alignItems="center">
                      <VStack>
                        <Text fontSize="xs" color="gray.500">Dernière mise à jour:</Text>
                        <Text fontSize="sm">
                          {format(new Date(lastLocation.timestamp), 'HH:mm:ss')}
                        </Text>
                      </VStack>
                      <IconButton
                        icon="google-maps"
                        size={24}
                        onPress={openInMaps}
                      />
                    </HStack>
                  ) : (
                    <Center py={4}>
                      <Spinner size="sm" />
                      <Text mt={2} fontSize="sm" color="gray.500">
                        En attente de la position du conducteur...
                      </Text>
                    </Center>
                  )}
                </VStack>
              )}
            </Card.Content>
          </Card>

          {/* Contact Driver */}
          {trip.guestDriverPhone && (
            <Card>
              <Card.Title title="Contacter le conducteur" />
              <Card.Content>
                <HStack justifyContent="space-between" alignItems="center">
                  <Text>{trip.guestDriverPhone}</Text>
                  <IconButton
                    icon="phone"
                    size={24}
                    onPress={() => Linking.openURL(`tel:${trip.guestDriverPhone}`)}
                  />
                </HStack>
              </Card.Content>
            </Card>
          )}
        </VStack>
      </ScrollView>
    </>
  );
}

export default TripTrackingScreen;
